import { useTranslations } from "../../hooks/useTranslations.ts";
import type { Hit as InstantsearchHit } from "instantsearch.js";
import type { SendEventForHits } from "instantsearch.js/es/lib/utils";
import { useEffect, useRef } from "react";
import { useConfigContext } from "../../ConfigProvider.tsx";
import { useUIContext } from "../providers/UIProvider.tsx";
import { useFormattedCustomerPrice } from "../../hooks/useFormattedCustomerPrice.tsx";
import { useFormattedCrossPrice } from "../../hooks/useFormattedCrossPrice.tsx";
import { usePriceOverrideContext } from "../providers/PriceOverrideProvider.tsx";
import aa from "search-insights";

export default function ItemBox({
									hit,
									sendEvent,
								}: {
	hit: InstantsearchHit<ItemboxAttributes>;
	sendEvent?: SendEventForHits;
}) {
	const { algoliaConfig } = useConfigContext();
	const tc = useTranslations();
	const { hasScrolledRef } = useUIContext();
	const itemRef = useRef<HTMLLIElement | null>(null);
	const itemLink = hit.itemLink;

	const { getPriceData } = usePriceOverrideContext();
	const priceData = getPriceData(hit);
	const formattedCustomerPrice = useFormattedCustomerPrice(priceData);
	const formattedCrossPrice = useFormattedCrossPrice(priceData);

	useEffect(() => {
		if (typeof window === "undefined") return;
		const hash = decodeURIComponent(window.location.hash.replace("#", ""));
		if (
			!hasScrolledRef.current &&
			hash === "item" + hit.objectID &&
			itemRef.current
		) {
			itemRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
			hasScrolledRef.current = true;
		}
	}, [hasScrolledRef, hit.objectID]);

	if (!itemLink || !tc) return null;

	const handleClick = () => {
		if (sendEvent) {
			// PLP/search context: the InstantSearch insights middleware already
			// sends clickedObjectIDsAfterSearch (with queryID/position) for us.
			sendEvent("click", hit, "PLP: Product Clicked");
			return;
		}

		// Recommendation widgets context (no InstantSearch insights middleware,
		// no queryID available): use clickedObjectIDs against the primary search
		// index — Algolia Recommend trains its models from events on that index,
		// not the (separate) recommend index, so events must land there to be
		// usable for training and to link up with other events for this object.
		aa("clickedObjectIDs", {
			eventName: "Itemcard opened",
			index: `${algoliaConfig.algoliaIndexName}`,
			objectIDs: [hit.objectID],
		});
	};


	return (
		<li
			ref={itemRef}
			key={hit.objectID}
			id={"item_" + hit.itemId}
			className="ais-itemBox itembox itemlist"
			data-object-id={hit.objectID}
			data-itemno={hit.itemNo}
			data-item-id={hit.itemId}
		>
			<a
				href={itemLink}
				title={hit.description}
				data-gtm-item={hit.itemId}
				data-itemlink={itemLink}
				className="itembox__main gtm-product-click"
				onClick={handleClick}
				onAuxClick={handleClick}
			>
				<div className="itemlistContent">
					<div className="itemlist_description_wrapper">
						<div className="itemlist_top">
							<div
								className="itemlist_name"
								dangerouslySetInnerHTML={{
									__html: hit._highlightResult.description.value as string,
								}}
							/>
							<div className="itemlist_description">{hit.summary}</div>
						</div>
						<div className="itemlist_bottom">
							<div className="itemlist_bottom_details">
								{hit["attributes.Ort"]?.length && (
									<div className="itemlist_location">
										<i className="iconNew iconNew-map-pin" />
										<span>{hit["attributes.Ort"].join(", ")}</span>
									</div>
								)}

								{hit["attributes.Zeitraum"]?.length > 0 && (
									<div className="itemlist_date">
										<i className="iconNew iconNew-calendar-filter" />
										<span>{hit["attributes.Zeitraum"].join(", ")}</span>
									</div>
								)}
							</div>

							{hit.promotions?.length > 0 && (
								<div className="itemlist_promotions">
									{tc.available_as}:{" "}
									{hit.promotions
										.map((p: { description?: string }) =>
											p.description ?? String(p)
										)
										.join(", ")}
								</div>
							)}
						</div>
					</div>
					<div className="itemlist_bottom_wrapper">
						<div className="itemlist_price_wrapper">
							<div className="itemlist_content prices">
								{formattedCustomerPrice &&
									(formattedCrossPrice ? (
										<>
                      <span className="cross_price">
                        {formattedCrossPrice}
                      </span>
											<span className="base_price">
                        {formattedCustomerPrice}
												<span>{tc.per_person}</span>
                      </span>
										</>
									) : (
										<span className="base_price">
                      {formattedCustomerPrice}
											<span>{tc.per_person}</span>
                    </span>
									))}
							</div>
						</div>
						<div className="button button_action">{tc.view_details}</div>
					</div>
				</div>
			</a>
		</li>
	);
}